import { logger } from '@hatchd/utils';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { UsersApi } from 'api/client';
import { ReactComponent as DeleteIcon } from 'assets/icons/icon-trash.svg';
import {
  AlertDialogActionsWrapper,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogLabel,
} from 'components/alert-dialog';
import { GlobalError } from 'components/auth-form/auth-form.styles';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import useAuth from 'hooks/use-auth';
import React, { FC, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError } from 'utils/helpers';
import * as yup from 'yup';
import { StyledAlertDialogOverlay } from './delete-account-button.styles';

type AccountDeletionFormData = {
  acknowledged: boolean;
  accountDeletionGlobalError?: string;
};

const accountDeletionSchema = yup.object().shape({
  acknowledged: yup
    .boolean()
    .required()
    .oneOf([true], 'Please acknowledge consequences.'),
});

const DeleteAccountButton: FC = () => {
  const [showDialog, setShowDialog] = useState(false);
  const cancelRef = useRef(null);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);
  const { token, logout } = useAuth();
  const UserApi = new UsersApi({
    apiKey: token ?? '',
  });

  const {
    register: registerAcknowledgement,
    handleSubmit: handleAccountDeletionSubmit,
    errors: accountDeletionErrors,
    formState: accountDeletionFormState,
    setError: setAccountDeletionError,
  } = useForm<AccountDeletionFormData>({
    mode: 'onChange',
    defaultValues: {
      acknowledged: false,
    },
    resolver: yupResolver(accountDeletionSchema),
  });

  const { isSubmitting: accountDeletionSubmitting, isValid } =
    accountDeletionFormState;

  const onDeleteAccountSubmit = handleAccountDeletionSubmit(
    async ({ accountDeletionGlobalError, ...data }) => {
      try {
        await UserApi.deleteUser();
        logout();
      } catch (error) {
        logger.error('Error deleting account', error);
        resolveApiError(
          error,
          setAccountDeletionError,
          'accountDeletionGlobalError',
          'Error deleting account'
        );
      }
    }
  );
  return (
    <>
      <Button variant='secondary' onClick={open}>
        <DeleteIcon />
        Delete account
      </Button>

      {showDialog && (
        <StyledAlertDialogOverlay leastDestructiveRef={cancelRef}>
          <AlertDialogContent>
            <AlertDialogLabel>Delete Account</AlertDialogLabel>
            <AlertDialogDescription>
              <form
                id='deleteAccountForm'
                noValidate
                onSubmit={onDeleteAccountSubmit}
              >
                <p>Deleting your account has the following effects:</p>
                <ul>
                  <li>You will be logged out immediately.</li>
                  <li>Your account will be deleted immediately.</li>
                  <li>
                    All your data, i.e. plans, will be deleted after 14 days.
                  </li>
                </ul>
                <Checkbox
                  bold
                  label='I understand the implications of deleting my account.'
                  id='acknowledged'
                  name='acknowledged'
                  ref={registerAcknowledgement}
                  value='true'
                />
                <div role='alert'>
                  <ErrorMessage
                    errors={accountDeletionErrors}
                    name='accountDeletionGlobalError'
                    as={GlobalError}
                  />
                </div>
              </form>
            </AlertDialogDescription>
            <AlertDialogActionsWrapper>
              <Button ref={cancelRef} onClick={close} variant='secondary'>
                Cancel
              </Button>
              <Button
                form='deleteAccountForm'
                type='submit'
                disabled={!isValid || accountDeletionSubmitting}
              >
                Delete account
              </Button>
            </AlertDialogActionsWrapper>
          </AlertDialogContent>
        </StyledAlertDialogOverlay>
      )}
    </>
  );
};

export default DeleteAccountButton;
